import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { Seo } from "../components/site/Seo";
import { CTASection } from "../components/site/CTASection";
import { getService } from "../data/services";
import NotFound from "./NotFound";

const SITE = "https://matrivon.com";

export default function ServiceLanding() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/+/, "").replace(/\/+$/, "");
  const s = getService(slug);
  if (!s) return <NotFound />;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: s.h1,
      description: s.metaDescription,
      serviceType: s.label,
      provider: { "@type": "Organization", name: "MATRIVON", url: SITE },
      areaServed: ["Middle East", "Turkey", "GCC", "CIS", "Europe"],
      url: `${SITE}/${s.slug}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE },
        { "@type": "ListItem", position: 2, name: s.label, item: `${SITE}/${s.slug}` },
      ],
    },
  ];

  return (
    <div>
      <Seo title={s.title} description={s.metaDescription} path={`/${s.slug}`} jsonLd={jsonLd} />
      <PageHero variant="dark" label={s.label} title={s.h1} intro={s.intro} crumbs={[{ label: s.label }]} />

      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[2fr_1fr]">
          <article className="space-y-10">
            {s.sections.map((sec) => (
              <section key={sec.h2}>
                <h2 className="font-heading text-2xl font-bold tracking-tight text-brand-ink">{sec.h2}</h2>
                <p className="mt-3 text-[17px] leading-relaxed text-brand-ink/85">{sec.body}</p>
                {sec.subs && (
                  <div className="mt-5 grid gap-px border border-brand-line bg-brand-line sm:grid-cols-2">
                    {sec.subs.map((sub) => (
                      <div key={sub.h3} className="bg-white p-5">
                        <h3 className="font-heading text-lg font-bold text-brand-ink">{sub.h3}</h3>
                        <p className="mt-2 text-[15px] leading-relaxed text-brand-muted">{sub.body}</p>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}

            <div className="border border-brand-blue/20 bg-brand-blue/5 p-6">
              <h2 className="font-heading text-xl font-bold text-brand-ink">Related expertise & resources</h2>
              <ul className="mt-4 space-y-2.5">
                {s.related.map((r) => (
                  <li key={r.to}>
                    <Link to={r.to} className="group inline-flex items-start gap-2 text-[15px] font-medium text-brand-blue hover:gap-2.5">
                      <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-brand-red" /> Explore {r.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </article>

          <aside className="space-y-8 lg:sticky lg:top-24 lg:self-start">
            <div className="border border-brand-line bg-brand-ink p-6 text-white">
              <h2 className="font-heading text-lg font-bold">{s.ctaLabel}</h2>
              <p className="mt-2 text-sm text-white/60">
                Tell us about your project and we will suggest how MATRIVON can help.
              </p>
              <Link to="/contact" data-testid="service-cta" className="mt-4 inline-flex w-full items-center justify-center gap-2 bg-brand-red px-5 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-px hover:brightness-110">
                {s.ctaLabel} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="border border-brand-line bg-white p-6">
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-brand-muted">Core expertise</h2>
              <ul className="space-y-2 text-sm text-brand-ink/85">
                {["Polyurethane systems","Silicone elastomers (RTV, LSR, HCR)","Specialty chemicals","Technical intelligence","Commercial advisory"].map((t) => (
                  <li key={t} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" />{t}</li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>

      <CTASection
        title={s.ctaLabel + "?"}
        primary={{ to: "/contact", label: s.ctaLabel }}
        secondary={{ to: "/technical-hub", label: "Explore Technical Hub" }}
      />
    </div>
  );
}
